import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Box, Typography } from '@material-ui/core';
import type { GroupedDimension } from '../../../../_models/schedule.model';
import { getChartPalette, useThemeVersion } from '../../../../theme/chartTheme';

interface ShiftUtilizationChartProps {
  groupedByShift: GroupedDimension[];
}

const ShiftUtilizationChart: React.FC<ShiftUtilizationChartProps> = ({ groupedByShift }) => {
  const [activeIndex, setActiveIndex] = React.useState<number | undefined>(undefined);
  const themeVersion = useThemeVersion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const COLORS = React.useMemo(() => getChartPalette(8), [themeVersion]);

  const data = groupedByShift.map((shift, index) => ({
    name: shift.name,
    value: shift.count,
    color: COLORS[index % COLORS.length],
    hours: shift.slots.reduce((acc, slot) => {
      const start = slot.startTime ? new Date(`2000-01-01T${slot.startTime}`) : null;
      const end = slot.endTime ? new Date(`2000-01-01T${slot.endTime}`) : null;
      if (start && end) {
        const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return acc + diff;
      }
      return acc;
    }, 0),
  }));

  const totalSessions = data.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const percentage = ((item.value / totalSessions) * 100).toFixed(1);
      return (
        <div
          style={{
            background: 'white',
            padding: '12px 16px',
            border: '1px solid var(--border-default)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 4 }}>
            {item.name}
          </Typography>
          <Typography variant="body2" style={{ color: 'var(--text-muted)', marginBottom: 2 }}>
            Sessions: {item.value} ({percentage}%)
          </Typography>
          <Typography variant="body2" style={{ color: 'var(--text-muted)' }}>
            Hours: {item.hours.toFixed(1)}h
          </Typography>
        </div>
      );
    }
    return null;
  };

  if (groupedByShift.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <Typography variant="body1" style={{ color: 'var(--text-muted)' }}>
          No shift data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <ResponsiveContainer width="100%" height={350}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={80}
            outerRadius={120}
            fill={COLORS[0]}
            dataKey="value"
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(undefined)}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                style={{
                  transform: activeIndex === index ? 'scale(1.05)' : 'scale(1)',
                  transition: 'transform 0.2s ease',
                }}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            iconType="circle"
            formatter={(value, entry: any) => {
              const item = data.find((d) => d.name === value);
              const percentage = item ? ((item.value / totalSessions) * 100).toFixed(1) : '0';
              return `${value} (${percentage}%)`;
            }}
          />
        </PieChart>
      </ResponsiveContainer>

      <Box mt={3}>
        <Typography
          variant="subtitle2"
          style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 16, textAlign: 'center' }}
        >
          Shift Distribution Summary
        </Typography>
        <Box display="flex" justifyContent="space-around" flexWrap="wrap" style={{ gap: '16px' }}>
          {data.map((item) => {
            const percentage = ((item.value / totalSessions) * 100).toFixed(1);
            const avgSessionsPerDay = (item.value / 7).toFixed(1); // Assuming weekly view
            return (
              <Box
                key={item.name}
                style={{
                  padding: '16px',
                  background: `linear-gradient(135deg, ${item.color}15 0%, ${item.color}05 100%)`,
                  border: `2px solid ${item.color}30`,
                  borderRadius: '12px',
                  minWidth: '140px',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Typography
                  variant="body2"
                  style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 8, fontSize: '13px' }}
                >
                  {item.name}
                </Typography>
                <Typography
                  variant="h5"
                  style={{ fontWeight: 700, color: item.color, marginBottom: 4 }}
                >
                  {item.value}
                </Typography>
                <Typography variant="caption" style={{ color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                  {percentage}% of total
                </Typography>
                <Typography variant="caption" style={{ color: 'var(--text-muted)', display: 'block' }}>
                  ~{avgSessionsPerDay} per day
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default ShiftUtilizationChart;
